import type { AsyncAction, PortalThunk } from '../../state'
import { portalAction, tokenSelector, convergenceCohortSelector } from '../../state'
import { UnsavedShippingAddress, ShippingAddress, UpdateShippingAddress } from '../../model'
import { unwrapResult } from '@reduxjs/toolkit'
import { isUndefinedOrEmpty, requiredAddressFieldsByCountry } from '@afterpay/utils'
import { Cohort } from '@afterpaytouch/portal-api'

const BASE_URL = '/portal/consumers/shipping-addresses'
const CASH_BASE_URL = (token: string): string => `/portal/consumers/shipping-addresses/${token}/cash`

const getBaseUrl = (cohort: Cohort | undefined, checkoutToken: string): string => {
  return cohort === Cohort.CashOnly ? CASH_BASE_URL(checkoutToken) : BASE_URL
}

export const getShippingAddresses: PortalThunk<void, ShippingAddress[]> = portalAction('consumer/shippingAddresses', async (_, thunk) => {
  const cohort = convergenceCohortSelector(thunk.getState())
  const checkoutToken = tokenSelector(thunk.getState())

  return await thunk.extra.portalApi().get(getBaseUrl(cohort, checkoutToken))
})

export const doSaveShippingAddress: PortalThunk<UnsavedShippingAddress, ShippingAddress> = portalAction(
  'consumer/saveShippingAddress',
  async (unSavedShippingAddress, thunk) => {
    const cohort = convergenceCohortSelector(thunk.getState())
    const checkoutToken = tokenSelector(thunk.getState())

    return await thunk.extra.portalApi().post(getBaseUrl(cohort, checkoutToken), unSavedShippingAddress)
  }
)

export const setPreferredShippingAddress: PortalThunk<number | string, number | string> = portalAction(
  'consumer/setPreferredShippingAddress',
  async (shippingAddressId, thunk) => {
    const cohort = convergenceCohortSelector(thunk.getState())
    const checkoutToken = tokenSelector(thunk.getState())
    await thunk.extra.portalApi().put(`${getBaseUrl(cohort, checkoutToken)}/${shippingAddressId}/preferred`, null)

    return shippingAddressId
  }
)

export const saveShippingAddress =
  (unsavedShippingAddress: UnsavedShippingAddress): AsyncAction<void> =>
  async (dispatch) => {
    const requiredFields = requiredAddressFieldsByCountry[unsavedShippingAddress.countryCode] ?? requiredAddressFieldsByCountry.DEFAULT

    // If the postcode isn't required, but hasn't been set, set it to the country code
    if (!requiredFields.postcode && isUndefinedOrEmpty(unsavedShippingAddress.postcode)) {
      unsavedShippingAddress.postcode = unsavedShippingAddress.countryCode
    }

    const savedAddress = unwrapResult(await dispatch(doSaveShippingAddress(unsavedShippingAddress)))
    if (unsavedShippingAddress.preferred) {
      dispatch(setPreferredShippingAddress(savedAddress.id))
    }
  }

export const doUpdateShippingAddress: PortalThunk<UpdateShippingAddress, void> = portalAction(
  'consumer/editShippingAddress',
  async (shippingAddress, thunk) => {
    const shippingAddressId = shippingAddress.shippingAddressId?.toString() ?? 'undefined'
    const cohort = convergenceCohortSelector(thunk.getState())
    const checkoutToken = tokenSelector(thunk.getState())

    return await thunk.extra.portalApi().put(`${getBaseUrl(cohort, checkoutToken)}/${shippingAddressId}/update`, shippingAddress.addressUpdateDetails)
  }
)

export const updateShippingAddress =
  (updatedShippingAddress: UpdateShippingAddress): AsyncAction<void> =>
  async (dispatch) => {
    unwrapResult(await dispatch(doUpdateShippingAddress(updatedShippingAddress)))
    await dispatch(getShippingAddresses())
  }
